<template>
  <div class="pricing-template-details-wrapper">
    <Breadcrumb :model="breadcrumbs" style="background: transparent"></Breadcrumb>
    <ProgressSpinner v-if="isLoading" />
    <Form v-if="!isLoading && pricingTemplate" :initial-values="formValues">
      <Card>
        <template #content>
          <FormField>
            <label for="name">Name</label>
            <InputGroup>
              <InputText id="name" type="text" name="name" required readonly />
            </InputGroup>
          </FormField>
          <FormField name="defaultPriceAmount">
            <label for="defaultPriceAmount">
              Base price
              <span class="tax-hint">tax excl.</span>
              <i
                v-tooltip.top="
                  'The price this template was created from, excluding taxes. Google Play converts it into a base price for every country, including local taxes. It cannot be changed.'
                "
                class="pi pi-info-circle base-price-info"
              />
            </label>
            <InputGroup>
              <InputGroupAddon>{{ pricingTemplate.defaultPriceCurrency }}</InputGroupAddon>
              <InputNumber id="defaultPriceAmount" :min-fraction-digits="2" required readonly />
            </InputGroup>
          </FormField>
        </template>
      </Card>
    </Form>
    <div class="tableActions">
      <Button
        v-tooltip="syncTooltip"
        :disabled="pricingTemplate?.linkedIAPs?.length! === 0 || changedPricePoints.length > 0"
        label="Sync IAPs"
        icon="pi pi-spinner"
        :loading="isSyncing"
        @click="syncPriceTemplate(pricingTemplate?.id!)"
      ></Button>
    </div>
    <Tabs value="0">
      <TabList>
        <Tab value="0">Linked IAPs</Tab>
        <Tab value="1">Pricing</Tab>
      </TabList>
      <TabPanels>
        <TabPanel value="0">
          <DataTable :value="pricingTemplate?.linkedIAPs">
            <template #empty>
              <div class="text-center">There are no IAPs linked yet!</div>
            </template>
            <Column header="IAP" field="iap.name" />
            <Column header="Product ID" field="iap.productId" class="field-iap-product-id">
              <template #body="{ data }">
                <img
                  v-if="data.iap.platform === 'ANDROID'"
                  hidden
                  class="iap-icon"
                  :src="GooglePlayConsoleIcon"
                  alt=""
                />
                <span class="iap-price">{{ data.iap.productId }}</span>
              </template>
            </Column>
            <Column header="Linked Date" field="createdAt">
              <template #body="{ data }">
                {{ formatDate(data.createdAt) }}
              </template>
            </Column>
          </DataTable>
        </TabPanel>
        <TabPanel value="1">
          <div class="tableActions pricing-actions">
            <div class="pricing-filters">
              <IconField>
                <InputIcon class="pi pi-search" />
                <InputText v-model="countrySearch" placeholder="Search country" />
              </IconField>
              <MultiSelect
                v-model="currencyFilter"
                :options="currencyOptions"
                option-label="label"
                option-value="currency"
                placeholder="All currencies"
                display="chip"
                :max-selected-labels="3"
                filter
                auto-filter-focus
                show-clear
                class="currency-filter"
              />
              <span class="filtered-count">
                {{ pricePointRows.length }} of {{ pricingTemplate?.pricePoints?.length ?? 0 }} countries
              </span>
            </div>
            <template v-if="isEditable">
              <span v-if="changedPricePoints.length > 0" class="pending-changes">
                {{ changedPricePoints.length }} unsaved change{{ changedPricePoints.length > 1 ? "s" : "" }}
              </span>
              <Button
                label="Discard Changes"
                severity="secondary"
                :disabled="changedPricePoints.length === 0 || isSaving"
                @click="resetDraftPrices()"
              />
              <Button
                v-tooltip="hasInvalidPrice ? 'All prices must be greater than 0' : ''"
                label="Save prices"
                icon="pi pi-save"
                :disabled="changedPricePoints.length === 0 || hasInvalidPrice"
                :loading="isSaving"
                @click="isConfirmSaveVisible = true"
              />
            </template>
          </div>
          <div v-if="isEditable" class="bulk-edit">
            <span class="bulk-edit-label">
              Apply to the {{ pricePointRows.length }} filtered countr{{ pricePointRows.length === 1 ? "y" : "ies" }}
            </span>
            <SelectButton
              v-model="bulkMode"
              :options="bulkModeOptions"
              option-label="label"
              option-value="value"
              option-disabled="disabled"
              :allow-empty="false"
            />
            <InputGroup v-if="bulkMode === 'set'" class="price-input">
              <InputNumber
                v-model="bulkValue"
                mode="decimal"
                :min="0"
                :min-fraction-digits="filteredCurrency ? currencyFractionDigits(filteredCurrency) : 0"
                :max-fraction-digits="filteredCurrency ? currencyFractionDigits(filteredCurrency) : 6"
                :invalid="!!bulkValueError"
                placeholder="New price"
                @input="onBulkValueInput"
              />
              <InputGroupAddon>{{ filteredCurrency }}</InputGroupAddon>
            </InputGroup>
            <InputGroup v-else class="price-input">
              <InputNumber
                v-model="bulkValue"
                mode="decimal"
                :min="-99.99"
                :max-fraction-digits="2"
                show-buttons
                :invalid="!!bulkValueError"
                placeholder="e.g. 10 or -5"
                @input="onBulkValueInput"
              />
              <InputGroupAddon>%</InputGroupAddon>
            </InputGroup>
            <Message v-if="bulkValueError" severity="error" size="small" variant="simple">
              {{ bulkValueError }}
            </Message>
            <Button
              label="Apply"
              icon="pi pi-check"
              severity="secondary"
              :disabled="!canApplyBulkEdit || isSaving"
              @click="applyBulkEdit()"
            />
            <Button
              label="Reset to base"
              icon="pi pi-replay"
              severity="secondary"
              text
              :disabled="pricePointRows.length === 0 || isSaving"
              @click="resetFilteredToBase()"
            />
          </div>
          <DataTable
            :value="pricePointRows"
            :row-class="pricePointRowClass"
            sort-field="countryName"
            :sort-order="1"
            class="price-points-table"
            size="small"
            scrollable
            scroll-height="65vh"
          >
            <Column header="Country" field="countryName" sortable>
              <template #body="{ data }">
                {{ data.countryName }}
                <span class="price-currency">{{ data.country }}</span>
              </template>
            </Column>
            <Column>
              <template #header>
                <span class="p-datatable-column-title">Base price</span>
                <span class="tax-hint">tax incl.</span>
                <i
                  v-tooltip.top="
                    'The price Google Play suggested for this country, converted from the template\'s base price. A price that differs from it is marked as Manual, and Reset to base brings it back.'
                  "
                  class="pi pi-info-circle base-price-info"
                />
              </template>
              <template #body="{ data }">
                {{ Number(data.basePriceAmountMicros) / 1000000 }}
                <span class="price-currency">{{ data.currency }}</span>
              </template>
            </Column>
            <Column>
              <template #header>
                <span class="p-datatable-column-title">Price</span>
                <span class="tax-hint">tax incl.</span>
              </template>
              <template #body="{ data }">
                <InputGroup v-if="isEditable" class="price-input">
                  <InputNumber
                    v-model="draftPrices[data.country]"
                    size="small"
                    mode="decimal"
                    :min="0"
                    :min-fraction-digits="currencyFractionDigits(data.currency)"
                    :max-fraction-digits="currencyFractionDigits(data.currency)"
                    :invalid="!isValidPrice(draftPrices[data.country])"
                    :disabled="isSaving"
                  />
                  <InputGroupAddon>{{ data.currency }}</InputGroupAddon>
                </InputGroup>
                <div v-else>
                  {{ Number(data.finalPriceAmountMicros) / 1000000 }}
                  <span class="price-currency">{{ data.currency }}</span>
                </div>
              </template>
            </Column>
            <Column>
              <template #body="{ data }">
                <Tag v-if="isPricePointChanged(data)" severity="warn" value="Edited" />
                <Tag v-else-if="data.isManualOverride" severity="info" value="Manual" />
              </template>
            </Column>
          </DataTable>
        </TabPanel>
      </TabPanels>
    </Tabs>
    <Dialog
      v-model:visible="isConfirmSaveVisible"
      modal
      :draggable="false"
      :closable="!isSaving"
      header="Confirm price changes"
      append-to="self"
      style="width: 760px"
    >
      <p class="confirm-save-summary">
        {{ pendingChanges.length }} countr{{ pendingChanges.length === 1 ? "y" : "ies" }} will be updated.
        <template v-if="(pricingTemplate?.linkedIAPs?.length ?? 0) > 0">
          The {{ pricingTemplate?.linkedIAPs?.length }} linked IAPs will be marked out of sync until you use "Sync
          IAPs".
        </template>
      </p>
      <DataTable
        :value="pendingChanges"
        class="price-points-table"
        size="small"
        scrollable
        scroll-height="50vh"
        sort-field="countryName"
        :sort-order="1"
      >
        <Column header="Country" field="countryName" sortable>
          <template #body="{ data }">
            {{ data.countryName }}
            <span class="price-currency">{{ data.country }}</span>
          </template>
        </Column>
        <Column header="Current price (tax incl.)">
          <template #body="{ data }">{{ formatPrice(data.currentPrice, data.currency) }}</template>
        </Column>
        <Column header="New price (tax incl.)">
          <template #body="{ data }">
            <b>{{ formatPrice(data.newPrice, data.currency) }}</b>
          </template>
        </Column>
        <Column header="Change" field="changePercentage" sortable>
          <template #body="{ data }">
            <span :class="data.changePercentage >= 0 ? 'price-increase' : 'price-decrease'">
              {{ data.changePercentage >= 0 ? "+" : "" }}{{ data.changePercentage.toFixed(2) }}%
            </span>
          </template>
        </Column>
      </DataTable>
      <template #footer>
        <Button label="Cancel" severity="secondary" :disabled="isSaving" @click="isConfirmSaveVisible = false" />
        <Button label="Confirm and save" icon="pi pi-save" :loading="isSaving" @click="confirmSavePrices()" />
      </template>
    </Dialog>
  </div>
</template>

<script setup lang="ts">
import usePricingTemplates from "@/modules/IAP/hooks/usePricingTemplates";
import { computed, ref, watch, watchEffect } from "vue";
import { onBeforeRouteLeave, useRoute, useRouter } from "vue-router";
import {
  Breadcrumb,
  Button,
  Card,
  Column,
  DataTable,
  Dialog,
  IconField,
  InputGroup,
  InputGroupAddon,
  InputIcon,
  InputNumber,
  InputText,
  Message,
  MultiSelect,
  ProgressSpinner,
  SelectButton,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Tag,
} from "primevue";
import { Form, FormField } from "@primevue/forms";
import links from "@/router/links";
import { formatDate } from "@/utils/formatDateTime";
import { formatPrice } from "@/utils/formatPrice";
import { PricingTemplate, PricingTemplateDetails } from "@/modules/IAP/types";
import GooglePlayConsoleIcon from "@/assets/icons/play-console.png";
import useToast from "@/hooks/useToastNotification";
import { useConfirm } from "primevue/useconfirm";

const { pricingTemplate, getPriceTemplateDetails, syncPricingTemplateIAPs, updatePricingTemplatePricePoints } =
  usePricingTemplates();
const { toastSuccess } = useToast();
const confirm = useConfirm();
const route = useRoute();
const router = useRouter();

const gameId = ref(route.params.gameId);
const formValues = ref<PricingTemplate>();
const isLoading = ref(false);
const isSyncing = ref(false);
const isSaving = ref(false);
const isConfirmSaveVisible = ref(false);
const countrySearch = ref("");
const currencyFilter = ref<string[]>([]);
const bulkMode = ref<"set" | "percentage">("percentage");
const bulkValue = ref<number | null>(null);
// Edited prices (in currency units) keyed by country, saved atomically with a single request
const draftPrices = ref<Record<string, number | null>>({});
const breadcrumbs = ref([
  {
    label: pricingTemplate.value?.appId ? "App Pricing Templates" : "Pricing Templates",
    command: () => {
      router.push({ name: pricingTemplate.value?.appId ? links.iap.appPricingTemplates : links.iap.pricingTemplates });
    },
  },
  { label: pricingTemplate.value?.name ?? "", disabled: true },
]);

const props = defineProps({
  templateId: String,
  readOnly: Boolean,
});

type PricePoint = PricingTemplateDetails["pricePoints"][number];

const regionNames = new Intl.DisplayNames(["en"], { type: "region" });

// System templates are shared across apps, only App templates prices can be edited
const isEditable = computed(() => !props.readOnly && pricingTemplate.value?.type === "APP");

const isValidPrice = (value: number | null | undefined) => value !== null && value !== undefined && value > 0;

const currencyOptions = computed(() => {
  const countByCurrency = new Map<string, number>();
  (pricingTemplate.value?.pricePoints ?? []).forEach(({ currency }) =>
    countByCurrency.set(currency, (countByCurrency.get(currency) ?? 0) + 1)
  );
  return [...countByCurrency.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([currency, count]) => ({ currency, label: `${currency} (${count})` }));
});

const pricePointRows = computed(() => {
  const search = countrySearch.value.trim().toLowerCase();
  return (pricingTemplate.value?.pricePoints ?? [])
    .map((pricePoint) => ({ ...pricePoint, countryName: regionNames.of(pricePoint.country) ?? pricePoint.country }))
    .filter((pricePoint) => currencyFilter.value.length === 0 || currencyFilter.value.includes(pricePoint.currency))
    .filter(
      (pricePoint) =>
        !search ||
        pricePoint.countryName.toLowerCase().includes(search) ||
        pricePoint.country.toLowerCase().includes(search)
    );
});

// Setting an absolute price only makes sense when all filtered countries share the same currency
const filteredCurrency = computed(() => {
  const currencies = new Set(pricePointRows.value.map(({ currency }) => currency));
  return currencies.size === 1 ? [...currencies][0] : null;
});

const bulkModeOptions = computed(() => [
  { label: "Adjust by %", value: "percentage" },
  { label: "Set price", value: "set", disabled: !filteredCurrency.value },
]);

// InputNumber only commits v-model on blur, its input event carries the parsed value on every keystroke
const onBulkValueInput = ({ value }: { value: unknown }) => {
  bulkValue.value = typeof value === "number" ? value : null;
};

// An empty input disables Apply without showing an error
const bulkValueError = computed(() => {
  if (bulkValue.value === null) {
    return null;
  }
  if (bulkMode.value === "set") {
    return isValidPrice(bulkValue.value) ? null : "Enter a price greater than 0";
  }
  if (bulkValue.value === 0) {
    return "Enter a non-zero percentage";
  }
  return bulkValue.value > -100 ? null : "Percentage must be greater than -100%";
});

const canApplyBulkEdit = computed(
  () => pricePointRows.value.length > 0 && bulkValue.value !== null && !bulkValueError.value
);

const resetBulkValue = () => {
  bulkValue.value = null;
};

const toMicros = (value: number) => Math.round(value * 1000000);

const currencyFractionDigits = (currency: string) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency }).resolvedOptions().maximumFractionDigits;

const isPricePointChanged = (pricePoint: PricePoint) => {
  const draft = draftPrices.value[pricePoint.country];
  return !isValidPrice(draft) || toMicros(draft!) !== Number(pricePoint.finalPriceAmountMicros);
};

const changedPricePoints = computed(() => (pricingTemplate.value?.pricePoints ?? []).filter(isPricePointChanged));

const hasInvalidPrice = computed(() =>
  changedPricePoints.value.some((pricePoint) => !isValidPrice(draftPrices.value[pricePoint.country]))
);

const pricePointRowClass = (pricePoint: PricePoint) => ({ "price-point-edited": isPricePointChanged(pricePoint) });

const syncTooltip = computed(() => {
  if (pricingTemplate.value?.linkedIAPs?.length === 0) {
    return "No links to sync";
  }
  if (changedPricePoints.value.length > 0) {
    return "Save or discard price changes before syncing";
  }
  return "";
});

const roundToCurrency = (value: number, currency: string) => Number(value.toFixed(currencyFractionDigits(currency)));

const applyBulkEdit = () => {
  if (!canApplyBulkEdit.value) {
    return;
  }
  const value = bulkValue.value!;
  pricePointRows.value.forEach(({ country, currency }) => {
    if (bulkMode.value === "set") {
      draftPrices.value[country] = value;
      return;
    }
    const current = draftPrices.value[country];
    if (isValidPrice(current)) {
      draftPrices.value[country] = roundToCurrency(current! * (1 + value / 100), currency);
    }
  });
  resetBulkValue();
};

const resetFilteredToBase = () => {
  pricePointRows.value.forEach(({ country, basePriceAmountMicros }) => {
    draftPrices.value[country] = Number(basePriceAmountMicros) / 1000000;
  });
};

// "Set price" gets disabled when the filter spans several currencies
// A price and a percentage are not interchangeable, start over when switching modes
watch(bulkMode, resetBulkValue);

watch(filteredCurrency, (currency) => {
  if (!currency && bulkMode.value === "set") {
    bulkMode.value = "percentage";
  }
});

const resetDraftPrices = () => {
  draftPrices.value = Object.fromEntries(
    (pricingTemplate.value?.pricePoints ?? []).map((pricePoint) => [
      pricePoint.country,
      Number(pricePoint.finalPriceAmountMicros) / 1000000,
    ])
  );
};

const pendingChanges = computed(() =>
  changedPricePoints.value.map((pricePoint) => {
    const currentPrice = Number(pricePoint.finalPriceAmountMicros) / 1000000;
    const newPrice = draftPrices.value[pricePoint.country]!;
    return {
      country: pricePoint.country,
      countryName: regionNames.of(pricePoint.country) ?? pricePoint.country,
      currency: pricePoint.currency,
      currentPrice,
      newPrice,
      changePercentage: ((newPrice - currentPrice) / currentPrice) * 100,
    };
  })
);

const savePrices = async () => {
  isSaving.value = true;
  const pricePoints = changedPricePoints.value.map((pricePoint) => ({
    country: pricePoint.country,
    finalPriceAmountMicros: toMicros(draftPrices.value[pricePoint.country]!),
  }));
  const saved = await updatePricingTemplatePricePoints(gameId.value as string, pricingTemplate.value!.id, pricePoints);
  if (saved) {
    resetDraftPrices();
    toastSuccess({
      detail:
        (pricingTemplate.value?.linkedIAPs?.length ?? 0) > 0
          ? `Updated ${pricePoints.length} prices. Use "Sync IAPs" to push them to the linked IAPs.`
          : `Updated ${pricePoints.length} prices.`,
      summary: "Success",
      life: 5000,
    });
  }
  isSaving.value = false;
  return saved;
};

// The dialog stays open on failure so the changes can be reviewed and retried
const confirmSavePrices = async () => {
  if (await savePrices()) {
    isConfirmSaveVisible.value = false;
  }
};

onBeforeRouteLeave((to, from, next) => {
  if (changedPricePoints.value.length === 0) {
    next();
    return;
  }
  confirm.require({
    message: "You have unsaved price changes. Leave and discard them?",
    header: "Unsaved changes",
    rejectClass: "p-button-secondary p-button-outlined",
    acceptClass: "p-button-danger",
    rejectLabel: "Stay",
    acceptLabel: "Discard changes",
    accept: () => next(),
    reject: () => next(false),
  });
});

const syncPriceTemplate = async (pricingTemplateId: string) => {
  isSyncing.value = true;
  if (await syncPricingTemplateIAPs(gameId.value as string, pricingTemplateId)) {
    toastSuccess({
      detail: `Successfully updated price to all IAPs linked to template ${pricingTemplate.value?.name}.`,
      summary: "Success",
      life: 5000,
    });
  }
  isSyncing.value = false;
};

const updateBreadcrumbs = () => {
  breadcrumbs.value = [
    {
      label: pricingTemplate.value?.appId ? "App Pricing Templates" : "Pricing Templates",
      command: () => {
        router.push({
          name: pricingTemplate.value?.appId ? links.iap.appPricingTemplates : links.iap.pricingTemplates,
        });
      },
    },
    { label: pricingTemplate.value?.name ?? "", disabled: true },
  ];
};

watchEffect(async () => {
  isLoading.value = true;
  await getPriceTemplateDetails(gameId.value as string, props.templateId as string);
  formValues.value = {
    ...pricingTemplate.value,
    defaultPriceAmount: (pricingTemplate.value?.defaultPriceAmountMicros ?? 0) / 1000000,
  };
  resetDraftPrices();
  updateBreadcrumbs();
  isLoading.value = false;
});
</script>

<style scoped lang="scss">
.pricing-template-details-wrapper {
  :deep(.field-iap-product-id) {
    display: flex;

    .iap-icon {
      height: 16px;
      margin: 2px;
    }
  }

  :deep(.tableActions) {
    display: flex;
    gap: 8px;
    justify-content: flex-end;
    align-items: center;
  }

  .pricing-actions {
    margin-bottom: 16px;

    .pricing-filters {
      display: flex;
      gap: 8px;
      align-items: center;
      margin-right: auto;
    }

    .currency-filter {
      min-width: 200px;
      max-width: 360px;
    }

    .filtered-count {
      font-size: 12px;
      color: var(--vd-color-text-default);
    }

    .pending-changes {
      color: var(--vd-color-text-default);
      font-size: 12px;
    }
  }

  .bulk-edit {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    align-items: center;
    margin-bottom: 16px;
    padding: 12px 14px;
    border: 1px solid var(--vd-color-text-light-7);
    border-radius: var(--vd-border-radius-2);

    .bulk-edit-label {
      font-weight: 600;
    }
  }

  .price-increase {
    color: var(--p-green-600);
  }

  .price-decrease {
    color: var(--p-red-600);
  }

  .tax-hint {
    margin-left: 4px;
    font-size: 11px;
    font-weight: 400;
    color: var(--vd-color-text-default);
  }

  .base-price-info {
    margin-left: 4px;
    font-size: 12px;
    color: var(--vd-color-text-default);
    cursor: help;
  }

  :deep(.price-input) {
    max-width: 220px;
  }

  :deep(.p-datatable .p-datatable-tbody tr.price-point-edited td) {
    background-color: var(--p-yellow-50);
  }

  :deep(.p-breadcrumb) {
    .p-breadcrumb-item-link {
      color: var(--vd-color-blue-default);
      font-weight: 700;
    }

    .p-disabled .p-breadcrumb-item-link {
      color: inherit;
      font-weight: 400;
    }
  }

  :deep(.p-card) {
    margin-bottom: 30px;
  }

  :deep(.p-tabs) {
    .p-tabpanels {
      background-color: transparent;
    }

    .p-tablist-tab-list {
      background-color: transparent;

      .p-tab {
        background-color: transparent;

        font-size: font("ML");
        font-weight: 600;
        line-height: 32px;
        padding: 0 16px;

        border: none;
      }

      .p-tablist-active-bar {
        height: 2px;
        background-color: $intense-blue;
      }
    }
  }

  :deep(.p-datatable) {
    border: 1px solid var(--vd-color-text-light-7);
    border-radius: var(--vd-border-radius-2);

    .p-datatable-tbody td {
      padding: 22px 14px;
    }

    .p-datatable-thead {
      th {
        background-color: var(--vd-color-text-light-9);
        padding: 12px 14px;
      }

      th:first-child {
        border-top-left-radius: var(--vd-border-radius-2);
      }

      th:last-child {
        border-top-right-radius: var(--vd-border-radius-2);
      }
    }

    .p-datatable-tbody tr:last-child {
      &:not(:hover) td {
        background-color: var(--vd-color-neutral-light);
      }

      td {
        border-bottom: none;
      }

      td:first-child {
        border-bottom-left-radius: var(--vd-border-radius-2);
      }

      td:last-child {
        border-bottom-right-radius: var(--vd-border-radius-2);
      }
    }

    .price-currency {
      font-weight: 700;
      font-size: 12px;
      color: var(--vd-color-text-default);
    }
  }

  :deep(.p-form) {
    .p-card-content {
      display: flex;
      gap: 24px;
    }

    .p-formfield {
      width: 100%;
    }

    label {
      display: block;
      margin-bottom: 8px;
    }
  }

  // Dense rows with a sticky header so the whole country list stays easy to scan and bulk edit
  :deep(.price-points-table) {
    .p-datatable-thead th {
      padding: 8px 12px;
    }

    .p-datatable-tbody td {
      padding: 4px 12px;
    }

    .p-inputgroupaddon {
      padding: 0 8px;
      min-width: auto;
      font-size: 12px;
    }

    .p-tag {
      padding: 2px 6px;
      font-size: 11px;
    }
  }
}
</style>
